import {
  parseInTimezone,
  parseISOWithOffset,
  parseToDateTime,
  toTimezone,
  addMinutes,
  formatTime as datetimeFormatTime,
  formatDate as datetimeFormatDate,
  strftimeToLuxonFormat
} from '@calendly/datetime'

import { i18n } from 'locales'

const ISO_OFFSET_PATTERN = /(Z|[+-]\d{2}:?\d{2})$/
const VANCOUVER_TIMEZONE = 'America/Vancouver'
const VANCOUVER_FIXED_OFFSET = 'UTC-7'
const VANCOUVER_PERMANENT_OFFSET_START = Date.parse('2026-11-01T09:00:00Z')
const TRUST_ISO_OFFSET_TIMEZONES = new Set([
  'Africa/Casablanca',
  'Africa/El_Aaiun'
])

export const parseLocalizedSpotTime = (time, timezone) => {
  if (typeof time === 'string' && ISO_OFFSET_PATTERN.test(time) && TRUST_ISO_OFFSET_TIMEZONES.has(timezone)) {
    // Morocco switched to permanent UTC on 2026-09-20 (tzdata 2026c). Honor the
    // backend offset so stale browser IANA data cannot shift 09:00+00:00 to 10:00.
    return parseISOWithOffset(time)
  }

  if (timezone !== VANCOUVER_TIMEZONE || typeof time !== 'string') {
    return parseInTimezone(time, timezone)
  }

  if (!ISO_OFFSET_PATTERN.test(time)) return parseInTimezone(time, timezone)

  const instant = parseToDateTime(time)
  if (!instant || instant.toMillis() < VANCOUVER_PERMANENT_OFFSET_START) {
    return parseInTimezone(time, timezone)
  }

  // Vancouver adopted permanent UTC−7 in 2026, but some supported browsers
  // still apply the former UTC−8 winter rule. This compatibility path can be
  // removed once supported runtimes include IANA tzdata 2026b or newer.
  return toTimezone(instant, VANCOUVER_FIXED_OFFSET)
}

export const formatDate = (time, format, locale) => {
  const luxonFormat = strftimeToLuxonFormat(format || i18n.t('time.formats.date_full'))
  const currentLocale = locale || i18n.locale
  return datetimeFormatDate(time, { format: luxonFormat, locale: currentLocale })
}

export const buildSpot = (startTime, duration, timezone) => {
  const start = parseLocalizedSpotTime(startTime, timezone)
  return {
    start_time: start,
    end_time: addMinutes(start, duration)
  }
}

export const formatEventTime = (spot, notation, timezone) => {
  const startTime = timezone ? parseLocalizedSpotTime(spot.start_time, timezone) : spot.start_time
  const endTime = timezone ? parseLocalizedSpotTime(spot.end_time, timezone) : spot.end_time
  const startDate = formatDate(startTime)
  const startFormatted = datetimeFormatTime(startTime, { notation })
  const endFormatted = datetimeFormatTime(endTime, { notation })
  return `${startFormatted} - ${endFormatted},\n ${startDate}`
}

export const formatEventTimeToDisplay = (spot, notation, timezone) => {
  const startTime = timezone ? parseLocalizedSpotTime(spot.start_time, timezone) : spot.start_time
  const endTime = timezone ? parseLocalizedSpotTime(spot.end_time, timezone) : spot.end_time
  return `${datetimeFormatTime(startTime, { notation })} - ${datetimeFormatTime(endTime, { notation })}`
}

export const formatEnterDetailsSpotHeadingLine = (spot, notation, timezone, locale) => {
  const startTime = timezone ? parseLocalizedSpotTime(spot.start_time, timezone) : spot.start_time
  const endTime = timezone ? parseLocalizedSpotTime(spot.end_time, timezone) : spot.end_time
  const dateFormat = i18n.t('time.formats.enter_details_spot_date')
  const datePart = formatDate(startTime, dateFormat, locale)
  const startFormatted = datetimeFormatTime(startTime, { notation })
  const endFormatted = datetimeFormatTime(endTime, { notation })
  return `${datePart}, ${startFormatted} – ${endFormatted}`
}

const formatShortTimezoneAbbrev = (dt, ianaTz, locale) => {
  if (!dt?.isValid || !ianaTz) return ''
  const loc = String(locale || 'en').replace('_', '-')
  try {
    const parts = new Intl.DateTimeFormat(loc, {
      timeZone: ianaTz,
      timeZoneName: 'short'
    }).formatToParts(dt.toJSDate())
    return parts.find((p) => p.type === 'timeZoneName')?.value?.trim() || ''
  } catch {
    return typeof dt.offsetNameShort === 'string' ? dt.offsetNameShort : ''
  }
}

/** Matches reschedule Enter Details screenshot: "Weekday, Mon D, YYYY, h:mm am — h:mm am (TZ)" */
export const formatRescheduleEnterDetailsRangeLine = (source, notation, timezoneId, locale) => {
  if (!source?.start_time || !source?.end_time || !timezoneId) return ''
  const startTime = parseLocalizedSpotTime(source.start_time, timezoneId)
  const endTime = parseLocalizedSpotTime(source.end_time, timezoneId)
  if (!startTime?.isValid || !endTime?.isValid) return ''
  const dateFormat = i18n.t('time.formats.enter_details_spot_date')
  const datePart = formatDate(startTime, dateFormat, locale)
  const startFormatted = datetimeFormatTime(startTime, { notation })
  const endFormatted = datetimeFormatTime(endTime, { notation })
  const tzAbbrev = formatShortTimezoneAbbrev(startTime, timezoneId, locale)
  const emDash = '\u2014'
  const tzPart = tzAbbrev ? ` (${tzAbbrev})` : ''
  return `${datePart}, ${startFormatted} ${emDash} ${endFormatted}${tzPart}`
}
